import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Alert, Grid, Paper, Typography,
  Box, Checkbox, CircularProgress,
} from '@mui/material';
import { policiesApi, type Policy } from '../../services/policiesApi';
import type { Incident } from '../../services/incidentsApi';

export interface IncidentThresholdDialogProps {
  open: boolean;
  onClose: () => void;
  selectedIncident: Incident | null;
  drawerBusy: string | null;
  canAdjust: boolean;
  scopedSiteId: string;
  scopedOrgId: string;
  onApply: (adjustment: Record<string, number>, autoDeploy: boolean) => Promise<void>;
  onOpenPolicyEditor: () => void;
}

const IncidentThresholdDialog: React.FC<IncidentThresholdDialogProps> = ({
  open, onClose, selectedIncident, drawerBusy, canAdjust,
  scopedSiteId, scopedOrgId, onApply, onOpenPolicyEditor,
}) => {
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [confidenceDraft, setConfidenceDraft] = useState('');
  const [persistenceDraft, setPersistenceDraft] = useState('');
  const [cooldownDraft, setCooldownDraft] = useState('');
  const [secondsDraft, setSecondsDraft] = useState('');
  const [autoDeploy, setAutoDeploy] = useState(true);

  useEffect(() => {
    if (!open || !selectedIncident?.incident_id) {
      setPolicy(null);
      setPolicyError(null);
      setPolicyLoading(false);
      return;
    }
    const policyId = String(
      (selectedIncident as any)?.triggered_by?.policy_id ||
      selectedIncident?.policy_id || ''
    ).trim();
    const siteId = String(selectedIncident?.site_id || scopedSiteId || '').trim();
    const orgId = String(selectedIncident?.org_id || scopedOrgId || '').trim();
    if (!policyId || !siteId) {
      setPolicy(null);
      setPolicyError('This incident is not linked to a tunable policy.');
      setPolicyLoading(false);
      return;
    }
    let cancelled = false;
    setPolicyLoading(true);
    setPolicyError(null);
    policiesApi.getPolicy(siteId, policyId, orgId || undefined)
      .then((p) => {
        if (cancelled) return;
        setPolicy(p || null);
        const cfg: any = (p as any)?.config || {};
        const timing: any = (p as any)?.timing || {};
        const conf =
          cfg.min_confidence ?? cfg.confidence_threshold ??
          (selectedIncident?.metrics as any)?.attributes?.confidence ??
          (selectedIncident?.metrics as any)?.confidence ??
          (selectedIncident?.metrics as any)?.score;
        const persistence = timing.persistence_seconds ?? cfg.persistence_seconds ?? '';
        const cooldown = timing.cooldown_seconds ?? cfg.cooldown_seconds ?? '';
        const threshold =
          p?.policy_type === 'dwell_violation' ? (cfg.max_dwell_seconds ?? '') :
          p?.policy_type === 'loitering' ? (cfg.max_loiter_seconds ?? '') : '';
        setConfidenceDraft(conf === '' || conf == null ? '' : String(conf));
        setPersistenceDraft(persistence === '' || persistence == null ? '' : String(persistence));
        setCooldownDraft(cooldown === '' || cooldown == null ? '' : String(cooldown));
        setSecondsDraft(threshold === '' || threshold == null ? '' : String(threshold));
        setAutoDeploy(true);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setPolicy(null);
        setPolicyError(e?.response?.data?.error || e?.message || 'Failed to load linked policy');
      })
      .finally(() => {
        if (!cancelled) setPolicyLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, selectedIncident, scopedSiteId, scopedOrgId]);

  const handleApply = () => {
    const adjustment: Record<string, number> = {};
    if (String(confidenceDraft).trim()) adjustment.confidence_threshold = Number(confidenceDraft);
    if (String(persistenceDraft).trim()) adjustment.persistence_seconds = Number(persistenceDraft);
    if (String(cooldownDraft).trim()) adjustment.cooldown_seconds = Number(cooldownDraft);
    if (String(secondsDraft).trim()) adjustment.threshold_seconds = Number(secondsDraft);
    if (!Object.keys(adjustment).length) {
      setPolicyError('Provide at least one threshold or timing value.');
      return;
    }
    onApply(adjustment, autoDeploy);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Adjust threshold from incident</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Tune the linked policy from this incident, then optionally deploy the policy immediately.
        </Alert>
        {policyError && (
          <Alert severity="warning" sx={{ mb: 2 }}>{policyError}</Alert>
        )}
        {policyLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="textSecondary">Loading linked policy…</Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="textSecondary">Policy</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {String(
                    policy?.name ||
                    (selectedIncident as any)?.triggered_by?.policy_name ||
                    policy?.policy_id ||
                    (selectedIncident as any)?.policy_id ||
                    'Not linked'
                  )}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="textSecondary">Policy type</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {String(policy?.policy_type || (selectedIncident as any)?.policy_type || 'unknown')}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="Confidence threshold"
                value={confidenceDraft} onChange={(e) => setConfidenceDraft(e.target.value)}
                inputProps={{ min: 0, max: 1, step: 0.01 }} helperText="Used for PPE / detection confidence." />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="Persistence seconds"
                value={persistenceDraft} onChange={(e) => setPersistenceDraft(e.target.value)}
                inputProps={{ min: 0, step: 0.1 }} helperText="Require a stable condition before triggering." />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="number" label="Cooldown seconds"
                value={cooldownDraft} onChange={(e) => setCooldownDraft(e.target.value)}
                inputProps={{ min: 0, step: 1 }} helperText="Suppress repeat alerts for this long." />
            </Grid>
            {(policy?.policy_type === 'dwell_violation' || policy?.policy_type === 'loitering') && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="number"
                  label={policy.policy_type === 'dwell_violation' ? 'Max dwell seconds' : 'Max loiter seconds'}
                  value={secondsDraft} onChange={(e) => setSecondsDraft(e.target.value)}
                  inputProps={{ min: 0, step: 1 }} helperText="Primary incident threshold for this policy." />
              </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox checked={autoDeploy} onChange={(e) => setAutoDeploy(Boolean(e.target.checked))} />
                <Typography variant="body2">Deploy updated policy immediately</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Use this incident as the tuning anchor. If you need broader edits, open the full policy editor instead.
              </Typography>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="outlined" disabled={!selectedIncident} onClick={onOpenPolicyEditor}>
          Open full policy editor
        </Button>
        <Button variant="contained"
          disabled={!selectedIncident || !!drawerBusy || policyLoading || !canAdjust}
          onClick={handleApply}
        >
          Save adjustment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IncidentThresholdDialog;
